import { Component, computed, input, output } from '@angular/core';
import { cn } from '@gametailors/web-core/tailwind';
import { cva, type VariantProps } from 'class-variance-authority';
import { IconKlankvoetNormal } from '../../../icons/i-klankvoet-normal';
import { IconKlankvoetStom } from '../../../icons/i-klankvoet-stom';
import { TKey } from '../rid-syllabic-keyboard.utils';

const keyboardKeyVariants = cva(
  'font-family-din rounded-sm border-2 px-3 py-2 text-2xl font-normal text-text-primary bg-white hover:bg-surface-100 border-surface-300 cursor-pointer',
  {
    variants: {
      type: {
        GROUP: 'font-bold border-purple-300 bg-purple-50 hover:bg-purple-200',
        KLANK: '',
        SPACE: '',
        KLANKVOET_NORMAL: 'text-emerald-500',
        KLANKVOET_STOM: 'text-rose-500',
        RULE: 'font-bold text-purple-500 border-purple-500 border-dashed',
      },
      klankStyle: {
        default: '',
        longShort:
          'rounded-xs rounded-tl-none rounded-bl-none border-0 border-l-2 border-dotted border-purple-500 text-purple-500 font-bold',
        long: 'border-none rounded-xs',
        sjwa: 'italic text-purple-500',
      },
      disabled: {
        true: 'border-transparent text-slate-400 bg-transparent hover:bg-transparent',
        false: '',
      },
      highlight: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      { disabled: true, highlight: true, class: 'bg-purple-200 text-purple-500 hover:bg-purple-200' },
      { disabled: false, highlight: true, class: 'bg-purple-200 border-purple-500 text-purple-500 hover:bg-purple-300' },
      { disabled: false, class: 'cursor-pointer' },
      { disabled: true, class: 'cursor-default' },
    ],
    defaultVariants: {
      klankStyle: 'default',
      disabled: false,
      highlight: false,
    },
  },
);

type KeyboardKeyVariants = VariantProps<typeof keyboardKeyVariants>;

@Component({
  selector: 'rid-keyboard-key',
  imports: [IconKlankvoetNormal, IconKlankvoetStom],
  templateUrl: './rid-keyboard-key.html',
  host: {
    '[class.hidden]': 'hidden()',
  },
})
export class RidKeyboardKey {
  key = input.required<TKey>();
  hidden = input<boolean>(false);
  hasHighlight = input<boolean>(false);
  disabled = input<boolean>(false);

  pressed = output<void>();

  twClasses = computed<string>(() => {
    const key = this.key();
    return cn(
      keyboardKeyVariants({
        type: this.resolveType(key),
        klankStyle: this.resolveKlankStyle(key),
        disabled: this.disabled(),
        highlight: this.hasHighlight(),
      }),
    );
  });

  private resolveType(key: TKey): KeyboardKeyVariants['type'] {
    switch (key.type) {
      case 'KLANKVOET':
        return key.value === '>' ? 'KLANKVOET_NORMAL' : 'KLANKVOET_STOM';
      default:
        return key.type;
    }
  }

  private resolveKlankStyle(key: TKey): KeyboardKeyVariants['klankStyle'] {
    if (key.type !== 'KLANK' && key.type !== 'SPACE') return 'default';

    if (key.value.startsWith('(')) return 'longShort';
    if (key.value.length === 2 && key.value[0] === key.value[1]) return 'long';
    if (['sjwa', 'ig', 'lijk'].includes(key.value)) return 'sjwa';

    return 'default';
  }

  press() {
    if (this.disabled()) return;

    this.pressed.emit();
  }
}
